import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { ApiError } from '@/api/client';
import { waitForJob } from '@/api/jobs';
import { qk } from '@/api/queries';
import type { Website } from '@/api/types';
import { useSession } from '@/app/session';
import { Button, CapsLabel, FieldHint, Icon, Modal, ModalFooter, ModalHeader, Notch, Pill, Segmented, useModalTitleId, useToast } from '@/design-system';
import { cx } from '@/lib/cx';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { BuyResults, RenewRow } from './BuyPanel';
import { OperatorDrawer } from './OperatorDrawer';
import {
  connectDomain,
  demoOf,
  domainKey,
  domainQuery,
  DOMAIN_STEPS,
  domainsKey,
  domainsQuery,
  domainStep,
  failedStep,
  HOST_RE,
  normalizeHost,
  OPERATOR_WAITING,
  ORDER_RUNNING,
  ordersQuery,
  productionBinding,
  purchaseOf,
  RENEW_FROM_DAYS,
  setDemoAccess,
  setDemoSlug,
  SLUG_RE,
  slugOf,
  verifyDomain,
  zoneOf,
  type CustomDomain,
  type DemoDomain,
  type DomainBinding,
  type WebsiteDomains,
} from './api';
import s from './domains.module.css';

/** «sharq» from «Sharq Textile»: the search starts from the brand, as the design's does. */
function brandQuery(brand: string): string {
  return (brand.split(/\s+/)[0] ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '');
}

/**
 * /sites/:id/domains — «Домены» (DESIGN_SPEC §4.5): the demo subdomain card (copy in the Notch, the edit-slug
 * pencil beside it), the production domain with its lifecycle and DNS records, and «Купить домен» /
 * «Подключить свой». Buying (P7-B01) and connecting (P6-C02) keep the design's look and say when they come.
 */
export function DomainsTab() {
  const site = useOutletContext<Website>();
  const q = useQuery(domainsQuery(site.id));
  const [editing, setEditing] = useState(false);
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const demo = demoOf(q.data);
  const production = productionBinding(q.data);
  return (
    <div className={s.grid}>
      <div className={s.col}>
        <DemoCard site={site} demo={demo} onEdit={() => setEditing(true)} />
        <ProductionCard site={site} binding={production} />
      </div>
      <BuyConnectPanel site={site} production={production} demoZone={demo?.zone ?? zoneOf(demo?.hostname)} />
      <SlugModal site={site} demo={demo} open={editing} onClose={() => setEditing(false)} />
    </div>
  );
}

/** The demo binding's DNS and TLS come from the wildcard record and certificate (P5-C01). */
const WILDCARD = new Set(['wildcard', 'ok']);

function DemoCard({ site, demo, onEdit }: { site: Website; demo: DemoDomain | null; onEdit: () => void }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const { can } = useSession();
  const host = demo?.hostname ?? site.domain ?? null;
  const live = demo?.status === 'active';
  const copy = () => {
    if (!host) return;
    void navigator.clipboard?.writeText(host).catch(() => undefined);
    toast(t('demo.copied', { domain: host }));
  };
  const edit = () => (can('domain:connect') ? onEdit() : toast(t('slug.forbidden')));
  const access = demo?.access ?? null;
  const qc = useQueryClient();
  const toggleAccess = () => {
    if (!can('domain:connect')) return toast(t('slug.forbidden'));
    if (access?.forced) return toast(t('demo.access_forced', { count: access.without_consent }));
    setDemoAccess(site.id, !access?.enabled)
      .then((d) => {
        qc.setQueryData<WebsiteDomains>(domainsKey(site.id), (prev) => (prev ? { ...prev, demo: d } : prev));
        toast(d.access.required ? t('demo.access_on') : t('demo.access_off'));
      })
      .catch((e: unknown) => toast(errorText(e, tc)));
  };
  return (
    <section className={cx(s.card, s.dots)} aria-labelledby="demo-domain">
      <Notch
        actions={[
          { icon: 'copy', label: t('demo.copy'), onClick: copy },
          { icon: 'pencil', label: t('demo.edit'), onClick: edit },
        ]}
      />
      <div className={s.head}>
        <span id="demo-domain" className={s.label}>
          {t('demo.label')}
        </span>
        <span className={s.host}>{host ?? t('demo.none')}</span>
      </div>
      <div className={s.chips}>
        <span className={cx(s.chip, (live || WILDCARD.has(demo?.binding?.dns_state ?? '')) && s.chipOk)}>{t('demo.chips.wildcard_dns')}</span>
        <span className={cx(s.chip, (live || WILDCARD.has(demo?.binding?.tls_state ?? '')) && s.chipOk)}>{t('demo.chips.wildcard_tls')}</span>
        {/* D22 (additive): the chip switches the demo's access link; it stays on while an image lacks consent. */}
        <button
          type="button"
          className={cx(s.chip, s.chipButton, s.chipOk)}
          aria-pressed={!!access?.enabled}
          title={access?.forced ? t('demo.access_forced', { count: access.without_consent }) : t('demo.access_toggle')}
          onClick={toggleAccess}
        >
          {access?.required ? t('demo.chips.noindex') : t('demo.chips.noindex_only')}
        </button>
        <span className={s.chip}>{t('demo.chips.checkout')}</span>
      </div>
      <span className={s.note}>{t('demo.note')}</span>
    </section>
  );
}

function ProductionCard({ site, binding }: { site: Website; binding: DomainBinding | null }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  // P7-B01 (additive): a purchase on its way, and the renewal from 60 days before expiry.
  const orders = useQuery(ordersQuery(site.id)).data;
  const running = orders?.find((o) => o.kind === 'register' && ORDER_RUNNING.includes(o.status) && (!binding || o.domain === binding.hostname)) ?? null;
  // P7-B02 (additive): an order waiting for the operator (the assisted path, or one nobody could confirm) links to it.
  const waiting = orders?.find((o) => OPERATOR_WAITING.includes(o.status) && (!binding || o.domain === binding.hostname)) ?? null;
  const [operator, setOperator] = useState<string | null>(null);
  const openOrder = waiting && (
    <button type="button" className={s.inlineLink} onClick={() => setOperator(waiting.id)}>
      {t('operator.open')}
    </button>
  );
  const bought = purchaseOf(orders, binding?.hostname);
  const renewal = bought?.renewal && (bought.renewal.days_left <= RENEW_FROM_DAYS || bought.renewal.window !== 'open') ? bought : null;
  const at = domainStep(binding);
  const failed = failedStep(binding);
  const live = binding?.status === 'active';
  // The records and the last check come with the domain itself (P6-C01 `CustomDomainOut`).
  const detail = useQuery({ ...domainQuery(binding?.id ?? ''), enabled: !!binding }).data;
  // The list moves the status (the tab polls it); the records follow when it changes.
  const qc = useQueryClient();
  const bindingId = binding?.id;
  const status = binding?.status;
  useEffect(() => {
    if (bindingId) void qc.invalidateQueries({ queryKey: domainKey(bindingId) });
  }, [qc, bindingId, status]);
  const records = detail?.records ?? [];
  const reason = codeText(binding?.failure_reason ?? detail?.failure_reason, tc) ?? t('production.failed_unknown');
  return (
    <section className={s.card} aria-labelledby="production-domain">
      <div className={s.head}>
        <span id="production-domain" className={s.label}>
          {t('production.label')}
        </span>
        <span className={s.host}>{binding?.hostname ?? tc('dash')}</span>
      </div>
      <ol className={s.life} aria-label={t('production.life')}>
        {DOMAIN_STEPS.map((step, i) => (
          <li
            key={step}
            className={cx(s.lifeStep, (i < at || live) && s.lifeDone, i === at && !live && (failed ? s.lifeFailed : s.lifeCurrent))}
            aria-current={i === at ? 'step' : undefined}
          >
            <div className={s.lifeBar} />
            <span className={s.lifeName}>{step}</span>
          </li>
        ))}
      </ol>
      {running && (
        <span className={s.note}>
          {t('order.running', { domain: running.domain, status: t(`order.status.${running.status}`) })}
          {waiting?.id === running.id && openOrder}
        </span>
      )}
      {waiting && waiting.id !== running?.id && (
        <span className={s.note}>
          {t(waiting.kind === 'renew' ? 'operator.renew_waiting' : 'order.running', { domain: waiting.domain, status: t(`order.status.${waiting.status}`) })}
          {openOrder}
        </span>
      )}
      {/* P6-C02 (additive): a failed check names its step and the reason; «Проверить» starts it again. */}
      {failed && (
        <div className={s.failed} role="alert">
          <span className={s.failedLine}>{t('production.failed_at', { step: failed, reason })}</span>
          <span className={s.note}>{t('production.failed_hint')}</span>
        </div>
      )}
      {records.length ? (
        <ul className={s.dns}>
          {records.map((r) => (
            <li key={`${r.type}-${r.name}`} className={s.dnsRow}>
              <span className={s.dnsType}>{r.type}</span>
              <span className={s.dnsHost}>{r.name}</span>
              <span className={s.dnsValue}>{r.value}</span>
              <Pill tone={r.ok ? 'ok' : 'warn'} size="tagTight" className={s.dnsPill}>
                {r.ok ? t('production.found') : t('production.waiting')}
              </Pill>
            </li>
          ))}
        </ul>
      ) : (
        !binding && !running && <span className={s.note}>{t('production.none')}</span>
      )}
      {renewal && <RenewRow site={site} order={renewal} />}
      {operator && <OperatorDrawer orderId={operator} onClose={() => setOperator(null)} />}
    </section>
  );
}

/** An API code as its RU/UZ sentence (errors.<code>), or null. */
function codeText(code: string | null | undefined, tc: (k: 'dash') => string): string | null {
  if (!code) return null;
  return (tc as unknown as (k: string, o: Record<string, unknown>) => string)(`errors.${code}`, { defaultValue: code });
}

function BuyConnectPanel({ site, production, demoZone }: { site: Website; production: DomainBinding | null; demoZone: string }) {
  const { t } = useTranslation('domains');
  const [mode, setMode] = useState<'buy' | 'connect'>('buy');
  const [query, setQuery] = useState(() => brandQuery(site.organization.brand_name));
  return (
    <section className={s.panel} aria-label={t('panel.label')}>
      <Segmented
        variant="form"
        label={t('panel.label')}
        value={mode}
        onChange={setMode}
        options={[
          { value: 'buy', label: t('panel.buy') },
          { value: 'connect', label: t('panel.connect') },
        ]}
      />
      {mode === 'buy' ? (
        <BuyResults site={site} query={query} setQuery={setQuery} />
      ) : (
        <ConnectForm site={site} production={production} demoZone={demoZone} />
      )}
    </section>
  );
}

/**
 * «Подключить свой» (P6-C02): «Проверить» connects the typed domain (P6-C01: the production binding at
 * `pending_dns` with its records, shown in the card on the left), or, for the domain already bound, checks
 * its DNS now. The outcome is a line under the note; a refusal shows under the field.
 */
function ConnectForm({ site, production, demoZone }: { site: Website; production: DomainBinding | null; demoZone: string }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useSession();
  // A client domain still being connected is prefilled, so «Проверить» checks it again.
  const [value, setValue] = useState(() => (production?.kind === 'custom' && production.status !== 'active' ? production.hostname : ''));
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const allowed = can('domain:connect');

  const describe = (b: CustomDomain): string => {
    const records = b.records;
    if (b.status === 'failed') return t('connect.result_failed', { domain: b.hostname, reason: codeText(b.failure_reason, tc) ?? t('production.failed_unknown') });
    if (b.status === 'pending_tls' || b.status === 'verified' || b.status === 'active') return t(`connect.result_${b.status}`, { domain: b.hostname });
    return t('connect.result_pending_dns', { domain: b.hostname, found: records.filter((r) => r.ok).length, total: records.length });
  };
  const adopt = (b: CustomDomain) => {
    qc.setQueryData(domainKey(b.id), b);
    const { records: _r, check: _c, ...row } = b;
    qc.setQueryData<WebsiteDomains>(domainsKey(site.id), (prev) => (prev ? { ...prev, bindings: [row, ...prev.bindings.filter((x) => x.id !== b.id)] } : prev));
    void qc.invalidateQueries({ queryKey: domainsKey(site.id) });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!allowed) {
      toast(t('connect.forbidden'));
      return;
    }
    const host = normalizeHost(value);
    setValue(host);
    setResult(null);
    if (!HOST_RE.test(host)) return setProblem(t('connect.invalid'));
    if (host === demoZone || host.endsWith(`.${demoZone}`)) return setProblem(t('connect.demo_zone'));
    setProblem(null);
    setBusy(true);
    const again = !!production && production.hostname === host;
    // «Проверить» on the bound domain starts `domain.verify`; its outcome is read once the job ends.
    const check = (id: string) =>
      verifyDomain(id).then(async (r) => {
        if (!r.job_id) return r.domain;
        setResult(t('connect.checking', { domain: host }));
        await waitForJob(r.job_id).catch(() => undefined);
        return qc.fetchQuery({ ...domainQuery(r.domain.id), staleTime: 0 });
      });
    (again ? check(production.id) : connectDomain(site.id, host))
      .then((b) => {
        adopt(b);
        setResult(describe(b));
        toast(again ? describe(b) : t('connect.started', { domain: b.hostname }));
      })
      .catch((err: unknown) => (err instanceof ApiError && (err.status === 409 || err.status === 422) ? setProblem(errorText(err, tc)) : toast(errorText(err, tc))))
      .finally(() => setBusy(false));
  };

  const dnsHost = production?.kind === 'custom' ? production.dns_host : null;
  return (
    <form onSubmit={submit} noValidate className={s.connectForm}>
      <div className={s.field}>
        <Icon name="link" size={16} className={s.fieldIcon} />
        <input
          key="own"
          className={s.input}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setProblem(null);
          }}
          placeholder={t('panel.own_placeholder')}
          aria-label={t('panel.own')}
          aria-invalid={!!problem}
          autoComplete="off"
          spellCheck={false}
          inputMode="url"
        />
        <button type="submit" className={s.check} aria-busy={busy} aria-disabled={!allowed || undefined} title={allowed ? undefined : t('connect.forbidden')}>
          {t('panel.check')}
        </button>
      </div>
      {problem && (
        <span className={s.connectError} role="alert">
          {problem}
        </span>
      )}
      <span className={s.connectNote}>{t('panel.connect_note')}</span>
      {result && <span className={s.connectResult}>{result}</span>}
      {dnsHost && <span className={s.flow}>{t('connect.instructions', { host: t(`connect.dns_hosts.${dnsHost}`, { defaultValue: dnsHost }) })}</span>}
    </form>
  );
}

/** The edit-slug action (additive, P5-C01): the demo address's first label, under the demo zone. */
function SlugModal({ site, demo, open, onClose }: { site: Website; demo: DemoDomain | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const qc = useQueryClient();
  const toast = useToast();
  const host = demo?.hostname ?? site.domain ?? null;
  const zone = demo?.zone ?? zoneOf(host);
  const current = demo?.slug ?? (host ? slugOf(host) : (site.demo_slug ?? ''));
  const [slug, setSlug] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setSlug(current);
      setProblem(null);
    }
  }, [open, current]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = slug.trim().toLowerCase();
    if (!SLUG_RE.test(value)) {
      setProblem(t('slug.invalid'));
      return;
    }
    setBusy(true);
    setDemoSlug(site.id, value)
      .then((d) => {
        qc.setQueryData<WebsiteDomains>(domainsKey(site.id), (prev) => (prev ? { ...prev, demo: d } : prev));
        void qc.invalidateQueries({ queryKey: domainsKey(site.id) });
        void qc.invalidateQueries({ queryKey: qk.website(site.id) });
        toast(t('slug.saved', { domain: d.hostname ?? `${value}.${zone}` }));
        onClose();
      })
      .catch((err: unknown) => (err instanceof ApiError && (err.status === 409 || err.status === 422) ? setProblem(errorText(err, tc)) : toast(errorText(err, tc))))
      .finally(() => setBusy(false));
  };
  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('slug.title')} sub={t('slug.sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <form onSubmit={submit} noValidate className={s.slugForm}>
        <label className={s.slugLabel}>
          <CapsLabel>{t('slug.field')}</CapsLabel>
          <div className={s.slugField}>
            <input
              className={s.slugInput}
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setProblem(null);
              }}
              aria-label={t('slug.field')}
              aria-invalid={!!problem}
              aria-describedby="slug-hint"
              autoComplete="off"
              spellCheck={false}
              maxLength={63}
            />
            <span className={s.slugZone}>.{zone}</span>
          </div>
        </label>
        <span id="slug-hint">{problem ? <span className={s.slugError} role="alert">{problem}</span> : <FieldHint>{t('slug.hint')}</FieldHint>}</span>
        <ModalFooter padTop>
          <Button variant="secondary" size={46} onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button type="submit" size={46} aria-busy={busy}>
            {t('slug.save')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
